/**
 * Command-label shell wrappers and literal aliases (ticket 3525, upstream pin
 * e3e7cc3f, MIT), split out of the ported `commandLabel.ts`. Byte-faithful.
 *
 * @module workLogCommandWrappers
 */

import {
  NON_DESCRIPTIVE_SHELL_PROGRAMS,
  TERMINAL_SHELL_PROGRAMS,
  tokenizeShellCommand,
} from "./workLogCommandTokens";
import {
  commandWithoutLeadingShellComments,
  indexAfterShellRedirection,
  serializeShellTokens,
  splitFirstShellCommand,
  withoutShellLineContinuations,
} from "./workLogCommandSplit";
import { commandProgramNameInternal } from "./workLogCommandDispatcher";
import { staticProgramName } from "./workLogCommandPrograms";

// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function literalAssignmentProgram(
  token: string,
): { readonly name: string; readonly program: string | null } | null {
  const assignment = token.match(/^([A-Za-z_][A-Za-z0-9_]*)=(.*)$/su);
  if (!assignment) return null;
  const name = assignment[1]!;
  let value = assignment[2]!.trim();
  if (!value || /[$`]/u.test(value)) return { name, program: null };

  if (value.startsWith("(") && value.endsWith(")")) {
    const arrayTokens = tokenizeShellCommand(value.slice(1, -1));
    value = arrayTokens?.[0] ?? "";
  } else if (/\s/u.test(value) && !/[\\/]/u.test(value)) {
    return { name, program: null };
  }

  return { name, program: staticProgramName(value) };
}

function referencedCommandAlias(token: string): string | null {
  const reference = token.match(
    /^\$(?:([A-Za-z_][A-Za-z0-9_]*)|\{([A-Za-z_][A-Za-z0-9_]*)(?:\[@\])?\})$/u,
  );
  return reference?.[1] ?? reference?.[2] ?? null;
}

// Recover only literal aliases declared by an earlier top-level shell segment.
// This covers common `SSH=(ssh ...)` and `TOOL=/path/to/tool` forms without
// evaluating expansions or trying to model general shell state.
// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function literalCommandAliasProgramName(command: string): string | null {
  const aliases = new Map<string, string>();
  let remainingCommand: string | null = command;
  let controlFlowDepth = 0;

  for (let segmentCount = 0; remainingCommand && segmentCount < 64; segmentCount += 1) {
    const commandWithoutComments = commandWithoutLeadingShellComments(remainingCommand);
    if (commandWithoutComments === null) return null;
    const commandSplit = splitFirstShellCommand(commandWithoutComments);
    const tokens = tokenizeShellCommand(withoutShellLineContinuations(commandSplit.firstCommand));
    if (tokens === null) return null;

    let commandIndex = tokens[0] === "do" || tokens[0] === "then" ? 1 : 0;
    while (commandIndex < tokens.length) {
      const indexAfterRedirection = indexAfterShellRedirection(tokens, commandIndex);
      if (indexAfterRedirection !== null && indexAfterRedirection <= tokens.length) {
        commandIndex = indexAfterRedirection;
        continue;
      }
      if (/^[A-Za-z_][A-Za-z0-9_]*\+?=/u.test(tokens[commandIndex] ?? "")) {
        commandIndex += 1;
        continue;
      }
      break;
    }

    const aliasName = referencedCommandAlias(tokens[commandIndex] ?? "");
    const aliasedProgram = aliasName ? aliases.get(aliasName) : undefined;
    if (aliasedProgram) return aliasedProgram;

    const leadingToken = tokens[0];
    if (leadingToken === "fi" || leadingToken === "done" || leadingToken === "esac") {
      controlFlowDepth = Math.max(0, controlFlowDepth - 1);
    }
    if (
      leadingToken === "if" ||
      leadingToken === "for" ||
      leadingToken === "while" ||
      leadingToken === "until" ||
      leadingToken === "select" ||
      leadingToken === "case"
    ) {
      controlFlowDepth += 1;
    }

    if (controlFlowDepth === 0 && leadingToken === "unset") {
      for (const name of tokens.slice(1)) aliases.delete(name);
    }

    const assignmentStart = tokens[0] === "export" ? 1 : 0;
    const assignments = tokens.slice(assignmentStart).map(literalAssignmentProgram);
    if (
      controlFlowDepth === 0 &&
      assignments.length > 0 &&
      assignments.every((assignment) => assignment !== null)
    ) {
      for (const assignment of assignments) {
        if (assignment.program) aliases.set(assignment.name, assignment.program);
        else aliases.delete(assignment.name);
      }
    }

    remainingCommand = commandSplit.remainingCommand;
  }

  return null;
}

// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function wrappedShellCommandProgramName(
  wrapper: string,
  tokens: ReadonlyArray<string>,
  start: number,
  depth: number,
  remainingCommand: string | null,
  segmentsRemaining: number,
): string | null {
  let index = start;

  if (wrapper === "command") {
    while (index < tokens.length) {
      const option = tokens[index]!;
      if (option === "--") {
        index += 1;
        break;
      }
      if (!option.startsWith("-") || option === "-") break;
      if (option !== "-p") return null;
      index += 1;
    }
  } else if (wrapper === "builtin") {
    if (tokens[index] === "--") index += 1;
    else if (tokens[index]?.startsWith("-")) return null;
  } else if (wrapper === "exec") {
    while (index < tokens.length) {
      const option = tokens[index]!;
      if (option === "--") {
        index += 1;
        break;
      }
      if (option === "-a") {
        if (tokens[index + 1] === undefined) return null;
        index += 2;
        continue;
      }
      if (/^-a.+/u.test(option) || /^-[cl]+$/u.test(option)) {
        index += 1;
        continue;
      }
      if (option.startsWith("-") && option !== "-") return null;
      break;
    }
  }

  const wrappedTokens = tokens.slice(index);
  if (wrappedTokens.length === 0) return null;
  let targetIndex = 0;
  while (targetIndex < wrappedTokens.length) {
    const indexAfterRedirection = indexAfterShellRedirection(wrappedTokens, targetIndex);
    if (indexAfterRedirection === null || indexAfterRedirection > wrappedTokens.length) break;
    targetIndex = indexAfterRedirection;
  }
  const target = wrappedTokens[targetIndex];
  if (target && /^[A-Za-z_][A-Za-z0-9_]*\+?=/u.test(target)) return null;

  const wrappedProgram = commandProgramNameInternal(
    serializeShellTokens(wrappedTokens),
    depth + 1,
    wrapper === "exec" ? "exec" : "shell",
    segmentsRemaining,
  );
  if (wrappedProgram !== null) return wrappedProgram;

  if (
    wrapper !== "exec" &&
    target &&
    target === target.toLowerCase() &&
    NON_DESCRIPTIVE_SHELL_PROGRAMS.has(target) &&
    !TERMINAL_SHELL_PROGRAMS.has(target) &&
    remainingCommand
  ) {
    return commandProgramNameInternal(remainingCommand, depth, "shell", segmentsRemaining - 1);
  }
  return null;
}
